<template>
  <div class="app-container">
    <!-- 顶部 -->
    <AppHeader
      :languages="languages"
      :currentLanguage="currentLanguage"
      @toggle-menu="toggleMenu"
      @open-share-modal="openShareModal"
      @open-language-dialog="openLanguageDialog"
    />
    <!-- 侧边栏 -->
    <div
      class="sidebar-overlay"
      v-if="sidebarVisible"
      @click="closeSidebar"
    ></div>
    <div class="sidebar" :class="{ 'sidebar-open': sidebarVisible }">
      <div class="sidebar-header">
        <button class="close-btn" @click="closeSidebar">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- 使用新的MenuList组件 -->
      <MenuList
        @menu-click="handleSidebarMenuClick"
        @menu-loaded="handleMenuLoaded"
      />
    </div>

    <main class="main-content">
      <router-view />
    </main>

    <footer class="footer">
      <div class="footer-content">
        <div class="company-logo">
          <img
            src="./assets/static/ochrm-logo.png"
            alt="logo"
            class="brand-logo"
          />
        </div>

        <div class="contact-info">
          <div class="contact-section">
            <h4>{{ $t("footer.contactTitle") }}</h4>
            <p>📞 0572-2678666</p>
            <p>✉️ info@ochem.biz</p>
          </div>

          <div class="contact-section">
            <h4>{{ $t("footer.addressTitle") }}</h4>
            <p>{{ $t("footer.address") }}</p>
          </div>
        </div>
      </div>

      <div class="copyright">
        <p class="beian-info">
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            class="beian-link"
          >
            浙ICP备12024782号
          </a>
          <span class="separator">·</span>
          <a
            href="https://www.beian.gov.cn/portal/registerSystemInfo?recordcode=33059102000410"
            target="_blank"
            rel="noopener noreferrer"
            class="beian-link gongan-link"
          >
            <img
              src="./assets/static/gongan-logo.png"
              alt="公安备案"
              class="gongan-icon"
              height="20"
              loading="lazy"
            />
            浙公网安备33059102000410号
          </a>
        </p>
        <p>
          Copyright ©2017-2020 湖州欧美化学有限公司 / Huzhou OChem Chemical Co.,
          Ltd. All Rights Reserved
        </p>
      </div>
    </footer>

    <!-- 语言选择弹窗 -->
    <el-dialog
      v-model="languageDialogVisible"
      :title="$t('languageDialog.title')"
      width="500px"
      :before-close="closeLanguageDialog"
      class="language-dialog"
    >
      <div class="language-options">
        <div
          v-for="language in languages"
          :key="language.code"
          class="language-option"
          :class="{ active: language.code === currentLanguage }"
          @click="selectLanguage(language.code)"
        >
          <div class="language-flag">{{ language.flag }}</div>
          <div class="language-info">
            <h3>{{ language.name }}</h3>
            <p>{{ language.nativeName }}</p>
            <p class="description">{{ language.description }}</p>
          </div>
          <div class="language-status" v-if="language.code === currentLanguage">
            <i class="fas fa-check"></i>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 语言欢迎弹窗 -->
    <!-- <LanguageWelcome
      :show="showLanguageWelcome"
      @close="closeLanguageWelcome"
    /> -->
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import AppHeader from "./components/AppHeader.vue";
// import LanguageWelcome from "./components/LanguageWelcome.vue";
import MenuList from "./components/MenuList.vue";
import { setLanguage } from "./i18n/index.js";
import "/node_modules/flag-icons/css/flag-icons.min.css";

export default {
  name: "App",
  components: {
    AppHeader,
    // LanguageWelcome,
    MenuList,
  },
  setup() {
    const { locale, t } = useI18n();
    return { locale, t };
  },
  data() {
    return {
      sidebarVisible: false,
      languageDialogVisible: false,
      // showLanguageWelcome: false,
      currentLanguage: "zh-CN",
      menuLoading: false,
      menuItems: [],
      languages: [
        {
          code: "zh-CN",
          name: "简体中文",
          nativeName: "简体中文",
          flag: "🇨🇳",
          flagCode: "cn",
          description: "中国大陆地区使用的标准中文",
        },
        {
          code: "en-US",
          name: "English",
          nativeName: "English",
          flag: "🇺🇸",
          flagCode: "us",
          description: "Standard American English",
        },
        {
          code: "es-ES",
          name: "Español",
          nativeName: "Español",
          flag: "🇪🇸",
          flagCode: "es",
          description: "Standard Spanish",
        },
        {
          code: "ar-SA",
          name: "العربية",
          nativeName: "العربية",
          flag: "🇸🇦",
          flagCode: "sa",
          description: "اللغة العربية القياسية",
        },
        {
          code: "ja-JP",
          name: "日本語",
          nativeName: "日本語",
          flag: "🇯🇵",
          flagCode: "jp",
          description: "標準的な日本語",
        },
      ],
    };
  },

  computed: {
    // 保留原有的计算属性，但现在从API获取数据
  },
  watch: {
    // 监听i18n语言变化，保持currentLanguage同步
    "$i18n.locale": {
      handler(newLocale) {
        const locale = newLocale?.value || newLocale;
        if (locale && locale !== this.currentLanguage) {
          this.currentLanguage = locale;
        }
        // 语言变化时更新页面标题
        this.updatePageTitle();
      },
      immediate: true,
      deep: true,
    },
    // 监听路由变化，更新页面标题
    "$route.path": {
      handler() {
        this.updatePageTitle();
      },
      immediate: true,
    },
  },
  methods: {
    async detectAndSetLanguageByIP() {
      try {
        const resp = await fetch("https://ipapi.co/json");
        if (!resp.ok) throw new Error("Geo API failed");
        const data = await resp.json();
        console.log("Geo IP data:", data);

        const country =
          data && data.country ? String(data.country).toUpperCase() : "";
        const lang = this.mapCountryToLang(country);
        // 设置语言并记入本地
        setLanguage(lang);
        this.currentLanguage = lang;
        this.locale = lang;
        localStorage.setItem("language-selected", "true");
        localStorage.setItem("selected-language", lang);
        // 首次自动选择后展示欢迎弹窗
        // this.showLanguageWelcome = true;
      } catch (e) {
        console.warn("Geo detect failed, fallback EN", e);
        const fallback = "en-US";
        setLanguage(fallback);
        this.currentLanguage = fallback;
        this.locale = fallback;
        localStorage.setItem("language-selected", "true");
        localStorage.setItem("selected-language", fallback);
        // this.showLanguageWelcome = true;
      }
    },
    mapCountryToLang(cc) {
      // 严格按照图片中的列表定义
      // 中文（不分繁简字）：中国、新加坡、港澳台地区
      const zh = ["CN", "SG", "HK", "MO", "TW"]; // 注意：国家顺序与图片中文字描述一致
      // 西班牙语：西班牙、墨西哥、阿根廷、古巴、哥伦比亚、危地马拉、萨尔瓦多、洪都拉斯
      const es = ["ES", "MX", "AR", "CU", "CO", "GT", "SV", "HN"];
      // 阿拉伯语：沙特阿拉伯、埃及、伊拉克、阿联酋、卡塔尔、叙利亚、黎巴嫩、摩洛哥、阿尔及利亚、突尼斯
      const ar = ["SA", "EG", "IQ", "AE", "QA", "SY", "LB", "MA", "DZ", "TN"];
      // 英语国家列表（用于参考，逻辑上不需要判断，作为fallback）
      const en = ['GB', 'US', 'CA', 'AU', 'NZ', 'IE', 'BS', 'BB', 'GY', 'JM'];

      // 识别逻辑
      if (zh.includes(cc)) return "zh-CN"; // 中文
      if (cc === "JP") return "ja-JP"; // 日语（仅日本）
      if (es.includes(cc)) return "es-ES"; // 西班牙语
      if (ar.includes(cc)) return "ar-SA"; // 阿拉伯语
      if (en.includes(cc)) return "en-US";

      // 默认返回英语（包含英国、美国、加拿大等所有图片中列出的英语国家，以及其他未在列表中的国家）
      return "en-US";
    },
    toggleMenu() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    closeSidebar() {
      this.sidebarVisible = false;
    },
    navigateTo(path) {
      this.$router.push(path);
      this.closeSidebar();
    },
    /**
     * 处理侧边栏菜单点击事件
     */
    handleSidebarMenuClick(menuItem) {
      console.log("Menu clicked:", menuItem);
      // 菜单项已在MenuList组件内部处理路由跳转
      // 这里只需关闭侧边栏
      this.closeSidebar();
    },
    /**
     * 菜单加载完成的回调
     */
    handleMenuLoaded(menuItems) {
      console.log("Menu loaded:", menuItems);
      // 可以在这里做一些额外处理，比如更新应用状态
      this.menuItems = menuItems;
    },
    openLanguageDialog() {
      // 跳转到语言选择页面
      this.$router.push("/language-selection");
    },
    closeLanguageDialog() {
      this.languageDialogVisible = false;
    },
    // closeLanguageWelcome() {
    //   this.showLanguageWelcome = false;
    // },
    selectLanguage(languageCode) {
      // 使用统一的语言设置方法
      setLanguage(languageCode);

      // 更新本地状态
      this.currentLanguage = languageCode;
      this.locale = languageCode;

      this.closeLanguageDialog();

      // 显示成功消息
      this.$message({
        message: `${this.$t("languageDialog.switchSuccess")} ${
          this.languages.find((lang) => lang.code === languageCode).name
        }`,
        type: "success",
      });
    },
    // openShareModal(type = "general") {
    //   const currentUrl = window.location.href;

    //   let shareUrl = "";

    //   if (type === "facebook") {
    //     shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    //       currentUrl
    //     )}`;
    //   } else if (type === "linkedin") {
    //     shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
    //       currentUrl
    //     )}`;
    //   } else {
    //     // 默认分享到Facebook
    //     shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    //       currentUrl
    //     )}`;
    //   }

    //   // 直接打开新页面
    //   window.open(
    //     shareUrl,
    //     "_blank",
    //     "width=600,height=400,scrollbars=yes,resizable=yes"
    //   );
    // },

    getPageTitle() {
      // 根据当前路由获取页面标题
      const route = this.$route;
      const companyName = this.$t("companyName") || "Huzhou Ochem Chemical Co., Ltd.";
      let pageTitle = "";

      switch (route.path) {
        case "/":
          pageTitle = this.$t("home.title") || "首页";
          break;
        case "/about":
          pageTitle = this.$t("about.title") || "关于我们";
          break;
        case "/products":
          pageTitle = this.$t("products.title") || "产品介绍";
          break;
        case '/solventfree':
          pageTitle = this.$t("solventFreeProductsPage.title") || "无溶剂产品";
          break;
        case '/estersolvent':
          pageTitle = this.$t("esterSolventProductsPage.title") || "酯溶剂产品";
          break;
        case '/alcoholicsolvent':
          pageTitle = this.$t("alcoholicSolventProductsPage.title") || "醇溶剂产品";
          break;
        case "/materials":
          pageTitle = this.$t("materials.title") || "材料介绍";
          break;
        case "/news":
          pageTitle = this.$t("news.title") || "新闻中心";
          break;
        case "/support":
          pageTitle = this.$t("support.title") || "技术支持";
          break;
        case "/hr":
          pageTitle = this.$t("hr.title") || "人力资源";
          break;
        case "/contact":
          pageTitle = this.$t("contact.title") || "联系我们";
          break;
        default:
          pageTitle = "";
      }

      // 如果有页面标题，则拼接公司名，否则只显示公司名
      return pageTitle ? `${pageTitle} - ${companyName}` : companyName;
    },

    updatePageTitle() {
      // 更新浏览器地址栏标题
      const title = this.getPageTitle();
      document.title = title;
    },

    getPageDescription() {
      // 根据当前路由获取页面描述
      const route = this.$route;
      switch (route.path) {
        case "/":
          return (
            this.$t("home.meta.description") ||
            "湖州欧美化学有限公司 - 专业的化学产品制造商"
          );
        case "/about":
          return (
            this.$t("about.meta.description") ||
            "了解湖州欧美化学有限公司的发展历程和企业文化"
          );
        case "/products":
          return (
            this.$t("products.meta.description") || "浏览我们的化学产品系列"
          );
        case "/materials":
          return this.$t("materials.meta.description") || "查看我们的材料介绍";
        case "/news":
          return (
            this.$t("news.meta.description") || "获取最新的公司新闻和行业动态"
          );
        case "/support":
          return (
            this.$t("support.meta.description") || "获取技术支持和售后服务"
          );
        case "/hr":
          return (
            this.$t("hr.meta.description") || "了解我们的招聘信息和职业发展"
          );
        case "/contact":
          return this.$t("contact.meta.description") || "联系我们获取更多信息";
        default:
          return "湖州欧美化学有限公司 - 专业的化学产品制造商";
      }
    },
  },
  async mounted() {
    // 清理旧的localStorage键，避免冲突
    const oldLanguage = localStorage.getItem("userLanguage");
    if (oldLanguage && !localStorage.getItem("selected-language")) {
      localStorage.setItem("selected-language", oldLanguage);
      localStorage.removeItem("userLanguage");
    }

    // 首次进入网站：按 IP 自动选择语言（仅当没有任何选择记录时）
    const hasSelected = localStorage.getItem("language-selected") === "true";
    const savedBeforeDetect = localStorage.getItem("selected-language");
    if (!hasSelected && !savedBeforeDetect) {
      await this.detectAndSetLanguageByIP();
    }

    // 从本地存储获取用户语言设置，与i18n保持一致
    const currentI18nLocale = this.$i18n.locale.value || this.$i18n.locale;
    const savedLanguage = localStorage.getItem("selected-language");

    if (savedLanguage) {
      this.currentLanguage = savedLanguage;
      this.locale = savedLanguage;
    } else {
      // 如果没有保存的语言，使用i18n的当前语言
      this.currentLanguage = currentI18nLocale;
    }

    // 确保显示的语言与i18n实际使用的语言一致
    if (this.currentLanguage !== currentI18nLocale) {
      this.currentLanguage = currentI18nLocale;
    }

    // 页面加载时更新标题
    this.updatePageTitle();
  },
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: "Microsoft YaHei", Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  line-height: 1.6;
  min-width: 1200px;
  overflow-x: auto;
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* 中间品牌区域 */
.brand-section {
  display: flex;
  align-items: center;
  gap: 8px;
}
.brand-logo {
  display: block;
  max-width: 160px;
  max-height: 56px;
  width: auto;
  height: 56px;
  object-fit: contain;
  margin: 0 auto;
}

.brand-name {
  font-size: 20px;
  font-weight: bold;
  color: white;
  letter-spacing: 1px;
}

.atom-icon {
  font-size: 16px;
  color: white;
}

/* 右侧社交媒体区域 */
.social-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
}

.social-icon:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.social-icon i {
  font-size: 14px;
}

/* 语言选择器样式 */
.language-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.language-selector:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.language-flag {
  font-size: 12px;
  line-height: 1;
}

/* 语言选择弹窗样式 */
.language-dialog .el-dialog__header {
  text-align: center;
  padding: 20px 20px 0;
  border-bottom: none;
}

.language-dialog .el-dialog__title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.language-options {
  padding: 20px 0;
}

.language-option {
  display: flex;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  background: #f8f9fa;
}

.language-option:hover {
  background: #e9ecef;
  border-color: #409eff;
}

.language-option.active {
  background: #ecf5ff;
  border-color: #409eff;
}

.language-option .language-flag {
  font-size: 24px;
  margin-right: 15px;
  width: 30px;
  text-align: center;
}

.language-info {
  flex: 1;
}

.language-info h3 {
  font-size: 16px;
  margin: 0 0 5px 0;
  color: #333;
  font-weight: 600;
}

.language-info p {
  font-size: 14px;
  margin: 0 0 3px 0;
  color: #409eff;
  font-weight: 500;
}

.language-info .description {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.language-status {
  color: #67c23a;
  font-size: 16px;
}

@media (max-width: 768px) {
  /* language按钮在移动端宽度随文字自适应 */
  .language-selector {
    width: auto;
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    border-radius: 14px; /* 保持圆形设计 */
  }

  .language-flag {
    font-size: 12px;
  }

  .language-dialog {
    margin: 10px !important;
    width: calc(100% - 20px) !important;
  }
}

/* 侧边栏样式 */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  transition: opacity 0.3s ease;
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 380px;
  height: 100vh;
  background: linear-gradient(180deg, #2c2c2c 0%, #1a1a1a 100%);
  z-index: 1000;
  transition: transform 0.3s ease;
  overflow: hidden;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  transform: translateX(-100%);
}

.sidebar-open {
  transform: translateX(0);
}

.sidebar-header {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.main-content {
  flex: 1;
  width: 100%;
  min-height: calc(100vh - 70px);
}

.footer {
  background-color: #34495e;
  color: white;
  padding: 40px 20px 20px;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.company-logo .logo {
  font-size: 28px;
  font-weight: bold;
  color: white;
}

.contact-info {
  display: flex;
  gap: 120px;
}

.contact-section h4 {
  font-size: 16px;
  margin-bottom: 15px;
  color: white;
}

.contact-section p {
  font-size: 14px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
}

.contact-section a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s;
}

.contact-section a:hover {
  color: #409eff;
}

.copyright {
  max-width: 1400px;
  margin: 0 auto;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 20px;
}

.copyright p {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin: 5px 0;
}

.beian-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.beian-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.3s ease;
}

.beian-link:hover {
  color: rgba(255, 255, 255, 0.8);
}

.gongan-link {
  display: flex;
  align-items: center;
  gap: 4px;
}

.gongan-icon {
  width: 14px;
  height: 14px;
  vertical-align: middle;
}

.separator {
  color: rgba(255, 255, 255, 0.4);
  font-weight: normal;
}

/* 响应式设计 */
@media (min-width: 1200px) {
  .header {
    padding: 0 50px;
  }

  .brand-name {
    font-size: 22px;
  }

  .atom-icon {
    font-size: 18px;
  }
}

@media (min-width: 1400px) {
  .header {
    padding: 0 60px;
  }

  .brand-name {
    font-size: 24px;
  }

  .atom-icon {
    font-size: 20px;
  }
}

@media (max-width: 768px) {
  body {
    min-width: auto;
  }

  .header {
    padding: 0 20px;
    height: 60px;
  }

  .brand-name {
    font-size: 18px;
  }

  .atom-icon {
    font-size: 14px;
  }

  .menu-text {
    font-size: 12px;
  }

  .hamburger-icon {
    width: 18px;
    height: 14px;
  }

  .social-icon {
    width: 20px;
    height: 20px;
  }

  .social-icon i {
    font-size: 12px;
  }

  .footer-content {
    flex-direction: column;
    gap: 30px;
    text-align: center;
  }

  .contact-info {
    flex-direction: column;
    gap: 30px;
  }

  .contact-section {
    text-align: center;
  }

  /* 移动端侧边栏样式 - 改为从上到下展开 */
  .sidebar {
    width: 100%;
    height: auto;
    max-height: 80vh;
    top: 0;
    left: 0;
    transform: translateY(-100%);
    border-radius: 0 0 20px 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  }

  .sidebar-open {
    transform: translateY(0);
  }

  .sidebar-header {
    top: 15px;
    right: 15px;
  }

  .close-btn {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .beian-info {
    flex-direction: column;
    gap: 6px;
  }

  .gongan-icon {
    width: 12px;
    height: 12px;
  }
}
</style>
